import { lazy, Suspense, useEffect } from "react";
import { initScrollDepthTracking } from "@/lib/gtm";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import OrganizationSchema from "./components/seo/OrganizationSchema";
const RedirectHandler = lazy(() => import("./components/RedirectHandler"));
const EnglishBanner = lazy(() => import("./i18n/EnglishBanner"));

// Lazy-load non-critical UI (toasts, cookie banner) to reduce main chunk
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const CookieBanner = lazy(() => import("./components/CookieBanner"));

// Lazy-loaded routes for code splitting
const NotFound = lazy(() => import("./pages/NotFound"));
const Etablissements = lazy(() => import("./pages/Etablissements"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const Location = lazy(() => import("./pages/Location"));
const Reserver = lazy(() => import("./pages/Reserver"));
const Livraison = lazy(() => import("./pages/Livraison"));
const Experiences = lazy(() => import("./pages/Experiences"));
const Professionnels = lazy(() => import("./pages/Professionnels"));
const APropos = lazy(() => import("./pages/APropos"));
const Faq = lazy(() => import("./pages/Faq"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const LocationVeloMezos = lazy(() => import("./pages/seo/LocationVeloMezos"));
const LocationVeloTown = lazy(() => import("./pages/seo/LocationVeloTown"));
const LocationSurf = lazy(() => import("./pages/LocationSurf"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const PlanDuSite = lazy(() => import("./pages/PlanDuSite"));
const Contact = lazy(() => import("./pages/Contact"));
const Cgv = lazy(() => import("./pages/Cgv"));
const LangLanding = lazy(() => import("./pages/i18n/LangLanding"));
// English pages (Lot 2 + 2-bis)
const HomeEn = lazy(() => import("./pages/en/Home"));
const AboutEn = lazy(() => import("./pages/en/About"));
const ExperiencesEn = lazy(() => import("./pages/en/Experiences"));
const FaqEn = lazy(() => import("./pages/en/Faq"));
const ContactEn = lazy(() => import("./pages/en/Contact"));
const LegalNoticeEn = lazy(() => import("./pages/en/LegalNotice"));
const PrivacyPolicyEn = lazy(() => import("./pages/en/PrivacyPolicy"));
const SitemapEn = lazy(() => import("./pages/en/Sitemap"));
const BookEn = lazy(() => import("./pages/en/Book"));
const DeliveryEn = lazy(() => import("./pages/en/Delivery"));
const ProfessionalsEn = lazy(() => import("./pages/en/Professionals"));
const PackagesEn = lazy(() => import("./pages/en/Packages"));
const LocationsEn = lazy(() => import("./pages/en/Locations"));
const TermsEn = lazy(() => import("./pages/en/Terms"));
const SurfRentalEn = lazy(() => import("./pages/en/SurfRental"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminLocations = lazy(() => import("./pages/admin/AdminLocations"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminRedirections = lazy(() => import("./pages/admin/AdminRedirections"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminTowns = lazy(() => import("./pages/admin/AdminTowns"));
const AdminSeoTowns = lazy(() => import("./pages/admin/AdminSeoTowns"));
const AdminEmailing = lazy(() => import("./pages/admin/AdminEmailing"));
const AdminSubscribers = lazy(() => import("./pages/admin/AdminSubscribers"));

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    const cleanup = initScrollDepthTracking();
    return cleanup;
  }, []);

  return (
  <HelmetProvider>
    <OrganizationSchema />
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <RedirectHandler />
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/formule-location-velo-landes" element={<Location />} />
              <Route path="/reserver" element={<Reserver />} />
              <Route path="/livraison" element={<Livraison />} />
              <Route path="/experiences" element={<Experiences />} />
              <Route path="/professionnels" element={<Professionnels />} />
              <Route path="/a-propos" element={<APropos />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogArticle />} />
              <Route path="/etablissements" element={<Etablissements />} />
              <Route path="/location-velo-mezos" element={<LocationVeloMezos />} />
              <Route path="/location-velo-mimizan" element={<LocationVeloTown />} />
              <Route path="/location-velo-lit-et-mixe" element={<LocationVeloTown />} />
              <Route path="/location-velo-bias" element={<LocationVeloTown />} />
              <Route path="/location-velo-leon" element={<LocationVeloTown />} />
              <Route path="/location-velo-moliets-et-maa" element={<LocationVeloTown />} />
              <Route path="/location-velo-azur" element={<LocationVeloTown />} />
              <Route path="/location-velo-castets" element={<LocationVeloTown />} />
              <Route path="/location-velo-levignacq" element={<LocationVeloTown />} />
              <Route path="/location-velo-saint-girons-plage" element={<LocationVeloTown />} />
              <Route path="/location-velo-taller" element={<LocationVeloTown />} />
              <Route path="/location-velo-saint-michel-escalus" element={<LocationVeloTown />} />
              <Route path="/location-velo-onesse-laharie" element={<LocationVeloTown />} />
              <Route path="/location-surf-contis" element={<LocationSurf />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
              <Route path="/plan-du-site" element={<PlanDuSite />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/cgv" element={<Cgv />} />
              {/* Multilingual landing pages (NL/DE/ES/IT) — kept as standalone landings */}
              <Route path="/nl" element={<LangLanding langCode="nl" />} />
              <Route path="/de" element={<LangLanding langCode="de" />} />
              <Route path="/es" element={<LangLanding langCode="es" />} />
              <Route path="/it" element={<LangLanding langCode="it" />} />
              {/* English routes (Lot 2 + 2-bis — fully translated pages) */}
              <Route path="/en" element={<HomeEn />} />
              <Route path="/en/about" element={<AboutEn />} />
              <Route path="/en/experiences" element={<ExperiencesEn />} />
              <Route path="/en/faq" element={<FaqEn />} />
              <Route path="/en/contact" element={<ContactEn />} />
              <Route path="/en/legal-notice" element={<LegalNoticeEn />} />
              <Route path="/en/privacy-policy" element={<PrivacyPolicyEn />} />
              <Route path="/en/sitemap" element={<SitemapEn />} />
              <Route path="/en/book" element={<BookEn />} />
              <Route path="/en/delivery" element={<DeliveryEn />} />
              <Route path="/en/professionals" element={<ProfessionalsEn />} />
              <Route path="/en/bike-rental-package" element={<PackagesEn />} />
              <Route path="/en/locations" element={<LocationsEn />} />
              <Route path="/en/terms" element={<TermsEn />} />
              <Route path="/en/surf-rental-contis" element={<SurfRentalEn />} />
              {/* English SEO town pages (bike rental by town) */}
              <Route path="/en/mimizan-bike-rental" element={<LocationVeloTown lang="en" />} />
              <Route path="/en/lit-et-mixe-bike-rental" element={<LocationVeloTown lang="en" />} />
              <Route path="/en/bias-bike-rental" element={<LocationVeloTown lang="en" />} />
              <Route path="/en/leon-bike-rental" element={<LocationVeloTown lang="en" />} />
              <Route path="/en/moliets-et-maa-bike-rental" element={<LocationVeloTown lang="en" />} />
              <Route path="/en/azur-bike-rental" element={<LocationVeloTown lang="en" />} />
              <Route path="/en/castets-bike-rental" element={<LocationVeloTown lang="en" />} />
              <Route path="/en/levignacq-bike-rental" element={<LocationVeloTown lang="en" />} />
              <Route path="/en/saint-girons-plage-bike-rental" element={<LocationVeloTown lang="en" />} />
              <Route path="/en/taller-bike-rental" element={<LocationVeloTown lang="en" />} />
              <Route path="/en/saint-michel-escalus-bike-rental" element={<LocationVeloTown lang="en" />} />
              <Route path="/en/onesse-laharie-bike-rental" element={<LocationVeloTown lang="en" />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="locations" element={<AdminLocations />} />
                <Route path="media" element={<AdminMedia />} />
                <Route path="villes" element={<AdminTowns />} />
                <Route path="seo-towns" element={<AdminSeoTowns />} />
                <Route path="messages" element={<AdminMessages />} />
                <Route path="emailing" element={<AdminEmailing />} />
                <Route path="abonnes" element={<AdminSubscribers />} />
                <Route path="redirections" element={<AdminRedirections />} />
              </Route>
              <Route path="/:locationSlug" element={<LocationPage />} />
              <Route path="/en/:locationSlug" element={<LocationPage lang="en" />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieBanner />
          <EnglishBanner />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
